import { v4 as uuid } from 'uuid';
import {
    CustomProduct,
    PaymentFlow,
    PaymentFlowType,
    PaymentMethod,
    Plan,
    PricingPage,
    Product,
    StripeMode,
} from '../types/pricingPage';
import { StripePrice } from '../types/stripePrice';

export function isStripePrice(product?: Product): product is StripePrice {
    return !!product && !!(product as StripePrice).product;
}

export function isCustomProduct(product: Product): product is CustomProduct {
    return (product as CustomProduct)?.price?.customLabel !== undefined;
}

const BETA_BILLING_SCHEMES = ['tiered'];
const SUPPORTED_BILLING_SCHEMES = ['per_unit', ...BETA_BILLING_SCHEMES];

export const isBetaBillingScheme = (product: Product) =>
    isStripePrice(product) && BETA_BILLING_SCHEMES.includes(product.billing_scheme);

export const isSupported = (product: Product) =>
    !isStripePrice(product) ||
    (SUPPORTED_BILLING_SCHEMES.includes(product.billing_scheme) && !product.transform_quantity);

export const getPricingName = (price: StripePrice) =>
    price.billing_scheme === 'per_unit' && !!price.transform_quantity ? 'package pricing' : price.billing_scheme;

export const isMonthly = ({ recurring }: StripePrice) => recurring?.interval === 'month';
export const isYearly = ({ recurring }: StripePrice) => recurring?.interval === 'year';

export const getMonthlyProducts = (products: Product[]) => products.filter(isStripePrice).filter(isMonthly);

export const getYearlyProducts = (products: Product[]) => products.filter(isStripePrice).filter(isYearly);

export function isRecurringProduct(product: Product) {
    return isStripePrice(product) && !!product.recurring;
}

export const toPlan = (product: Product, order: number, paymentFlow?: PaymentFlow): Plan => ({
    id: uuid(),
    order,
    name: isStripePrice(product) ? product.product.name : product.name,
    description: isStripePrice(product) ? product.product.description : '',
    stripeProductId: isStripePrice(product) ? product.product.id : null,
    stripePriceId: isStripePrice(product) ? product.id : null,
    customProduct: isCustomProduct(product) ? product : null,
    features: [{ id: uuid(), name: '', allPlans: false, visible: true, subdued: false, slug: '' }], //single empty feature to display an input in the form
    product,
    productType: isRecurringProduct(product) ? 'recurring' : 'one-off',
    designSettings: {
        id: uuid(),
        buttonText:
            isCustomProduct(product) && product.callToActionLabel
                ? product.callToActionLabel
                : isStripePrice(product) && !product.recurring
                ? 'Buy Now'
                : 'Get Started',
    },
    paymentFlow: paymentFlow
        ? paymentFlow
        : {
              id: uuid(),
              type: PaymentFlowType.CHARGE_NOW,
              paymentSuccessUrl: undefined,
              paymentFailureUrl: undefined,
              returnToOriginSite: true,
          },
    paymentSettings: { requireBillingAddress: false, requireShippingAddress: false, paymentMethod: ['card'] },
    subscriptionSettings: {
        expireAfterPeriods: null,
        trialDays: null,
        automaticTax: false,
        adjustableQuantity: false,
        coupons: false,
        skipPayment: { enabled: false, avoidDuplicates: false },
        resetBillingCycle: { enabled: false, dayOfMonth: 1 },
        enableAddons: false,
        chargeLater: false,
        embeddedUi: false,
        sendInvoice: false,
        collectTaxId: false,
    },
    paypalPlanId: product.paypalPlanId,
});

export const toPlans = (products: Product[]) => products.map((product, idx) => toPlan(product, idx));

export const getStripeMode = (status: PricingPage['status']) =>
    status === 'ACTIVE' ? StripeMode.LIVE : StripeMode.TEST;

export const getDummyPrice = (amountDollars: number): StripePrice => ({
    id: uuid(),
    billing_scheme: 'per_unit',
    unit_amount: amountDollars,
    currency: 'USD',
    recurring: { interval: 'month', interval_count: 1 },
    product: { id: 'Dummy', name: 'Dummy', description: '', images: [], active: true },
    tiers: [],
    livemode: false,
});

const methodsBySupportedCurrency: Map<PaymentMethod, string[]> = new Map();
methodsBySupportedCurrency.set('sepa_debit', ['EUR']);
methodsBySupportedCurrency.set('ideal', ['EUR']);
methodsBySupportedCurrency.set('bacs_debit', ['GBP']);
methodsBySupportedCurrency.set('acss_debit', ['USD', 'CAD']);
methodsBySupportedCurrency.set('klarna', ['GBP', 'EUR']);
methodsBySupportedCurrency.set('afterpay_clearpay', ['USD', 'GBP', 'CAD', 'AUD', 'NZD']);
methodsBySupportedCurrency.set('bancontact', ['EUR']);
methodsBySupportedCurrency.set('alipay', ['AUD', 'CAD', 'EUR', 'GBP', 'HKD', 'JPY', 'SGD', 'USD', 'NZD', 'MYR', 'CNY']);

const methodsWithRecurringSupport: PaymentMethod[] = ['sepa_debit', 'alipay'];

const methodsWithNotes: Map<PaymentMethod, string> = new Map();
methodsWithNotes.set('alipay', 'Using Alipay for subscriptions requires approval from Stripe.');

export const getPaymentMethodNotes = (method: PaymentMethod): string | undefined => methodsWithNotes.get(method);

export const isValidPaymentMethod = (method: PaymentMethod, price: StripePrice) => {
    if (methodsBySupportedCurrency.has(method)) {
        return (
            (methodsWithRecurringSupport.includes(method) || !price.recurring) &&
            methodsBySupportedCurrency.get(method)?.includes(price.currency.toUpperCase())
        );
    } else {
        return true;
    }
};
